import classNames from "classnames";

const sizeMap = { sm: 16, md: 20, lg: 28, xl: 40 } as const;

interface SpinnerProps {
  /** 16 / 20 / 28 / 40 px. Default `md` (20px) — matches the old CircleLoader size. */
  size?: keyof typeof sizeMap;
  /** Extra classes. Color comes from `currentColor`, so set text color here
   *  (e.g. `text-primary`) for standalone spinners. Inside a button it inherits
   *  the button's text color automatically. */
  className?: string;
  /** Accessible label announced to screen readers. */
  label?: string;
}

/**
 * The single app-wide loading spinner. Self-contained SVG (no external lib),
 * token-friendly (inherits `currentColor`), and replaces CircleLoader /
 * TailSpin / FaSpinner / lucide Loader. See docs/reference.md.
 */
const Spinner = ({ size = "md", className, label = "Loading" }: SpinnerProps) => {
  const px = sizeMap[size];
  return (
    <span
      role="status"
      aria-live="polite"
      className={classNames("inline-flex items-center justify-center", className)}
    >
      <svg
        width={px}
        height={px}
        viewBox="0 0 24 24"
        fill="none"
        className="animate-spin"
        aria-hidden="true"
      >
        <circle
          cx="12"
          cy="12"
          r="10"
          stroke="currentColor"
          strokeOpacity="0.25"
          strokeWidth="3"
        />
        <path
          d="M22 12a10 10 0 0 1-10 10"
          stroke="currentColor"
          strokeWidth="3"
          strokeLinecap="round"
        />
      </svg>
      <span className="sr-only">{label}</span>
    </span>
  );
};

export default Spinner;
